import { useCallback, useId, useState } from 'react'
import {
  getDocumentType,
  maskCnpj,
  maskCpf,
  onlyDigits,
  validateCnpj,
  validateCpf,
  type DocumentType,
} from './utils'
import styles from './CpfCnpjInput.module.css'

export type CpfCnpjInputMode = 'cpf' | 'cnpj' | 'auto'

export interface CpfCnpjInputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  /** 'cpf' | 'cnpj' | 'auto' (auto: começa como CPF, a partir de 12 dígitos vira CNPJ) */
  mode?: CpfCnpjInputMode
  value?: string
  onChange?: (value: string, rawDigits: string) => void
  onValidationChange?: (valid: boolean) => void
  /** Mensagem quando inválido (ex.: "CPF inválido") */
  invalidMessage?: string
}

function applyMask(value: string, type: DocumentType): string {
  return type === 'cnpj' ? maskCnpj(value) : maskCpf(value)
}

function validate(value: string, type: DocumentType): boolean {
  const d = onlyDigits(value)
  if (type === 'cpf' && d.length === 11) return validateCpf(value)
  if (type === 'cnpj' && d.length === 14) return validateCnpj(value)
  return false
}

export function CpfCnpjInput({
  mode = 'cpf',
  value: valueProp,
  onChange,
  onValidationChange,
  invalidMessage,
  className,
  id: idProp,
  placeholder,
  ...rest
}: CpfCnpjInputProps) {
  const id = useId()
  const inputId = idProp ?? id

  const [internalValue, setInternalValue] = useState('')
  const isControlled = valueProp !== undefined
  const rawValue = isControlled ? valueProp : internalValue
  const digits = onlyDigits(rawValue)
  const currentType: DocumentType = mode === 'auto' ? getDocumentType(digits) : mode
  const value = applyMask(digits, currentType)
  const isComplete =
    (currentType === 'cpf' && digits.length === 11) || (currentType === 'cnpj' && digits.length === 14)
  const isValid = isComplete && validate(value, currentType)

  const defaultPlaceholder =
    currentType === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'
  const displayPlaceholder = placeholder ?? defaultPlaceholder

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const d = onlyDigits(raw)
      const type: DocumentType = mode === 'auto' ? getDocumentType(d) : mode
      const maxLen = mode === 'auto' ? 14 : type === 'cnpj' ? 14 : 11
      const limited = d.slice(0, maxLen)
      const masked = applyMask(limited, type)

      if (!isControlled) setInternalValue(masked)
      onChange?.(masked, limited)

      const nowComplete =
        (type === 'cpf' && limited.length === 11) || (type === 'cnpj' && limited.length === 14)
      const nowValid = nowComplete && validate(masked, type)
      onValidationChange?.(nowValid)
    },
    [mode, isControlled, onChange, onValidationChange]
  )

  const showInvalid = isComplete && !isValid && value.length > 0

  return (
    <div className={styles.wrapper}>
      <input
        {...rest}
        id={inputId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={displayPlaceholder}
        value={value}
        onChange={handleChange}
        className={[styles.input, className, showInvalid && styles.inputInvalid].filter(Boolean).join(' ')}
        aria-invalid={showInvalid}
        aria-describedby={showInvalid ? `${inputId}-error` : undefined}
        maxLength={mode === 'auto' ? 18 : currentType === 'cnpj' ? 18 : 14}
      />
      {showInvalid && invalidMessage && (
        <span id={`${inputId}-error`} className={styles.error} role="alert">
          {invalidMessage}
        </span>
      )}
    </div>
  )
}
